import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Flex, notification, Radio, Table, TableColumnsType, Input, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { uniqueId } from 'lodash';
import { getStockFactor, actionStock } from '@/adapters/master';
import { IStockFactorRes, IApiResponse } from '@/interfaces/master';

interface IBatchRejection {
  status: 'rejected' | 'fulfilled';
  reason?: { refNo?: string; errors?: { errorMessage?: string } };
}

type Category = 'fertilizer' | 'chemical' | 'equipment';
type Mode = 'insert' | 'update';

const StockFactorPage: React.FC = () => {
  const [tab, setTab] = useState<Category>('fertilizer');
  const [mode, setMode] = useState<Mode>('insert');
  const [searchText, setSearchText] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selected, setSelected] = useState<any[]>([]);

  const { data, refetch, isFetching } = useQuery<IStockFactorRes, Error>({
    queryKey: ['stock-factor-list'],
    queryFn: getStockFactor,
    retry: 0,
  });

  useEffect(() => {
    refetch();
  }, [tab, mode, refetch]);

  useEffect(() => {
    const dataKey = `${tab}_${mode}` as keyof IStockFactorRes;
    const list = data?.[dataKey] || [];
    setSelected(list.map((item) => ({ ...item, _key: uniqueId('stk-') })));
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, [tab, mode, data]);

  const notifyStockErrors = (title: string, res: any, originalData: any[]) => {
    const errors: string[] = [];
    if (Array.isArray(res?.data)) {
      (res.data as IBatchRejection[]).forEach((result, index) => {
        if (result.status === 'rejected') {
          const item = originalData[index];
          const name = item?.V_FER_DESC || item?.V_CHEM_DESC2 || item?.V_EQPT_TYPE || `ลำดับที่ ${index + 1}`;
          errors.push(`${name}: ${result.reason?.errors?.errorMessage || 'ไม่ทราบสาเหตุ'}`);
        }
      });
    } else if (res?.status !== 'OK') {
      errors.push(res?.message || 'Server Error');
    }

    if (errors.length > 0) {
      notification.error({
        message: title,
        style: { width: '500px' },
        description: (
          <ul style={{ paddingLeft: '15px', margin: 0, maxHeight: '250px', overflowY: 'auto' }}>
            {errors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        ),
      });
      return true;
    }
    return false;
  };

  const stockMutation = useMutation({
    mutationFn: async ({ action, payload }: { action: 'Create' | 'Update' | 'Delete'; payload: any[] }) => {
      const categoryMap: Record<Category, 'Fertilizer' | 'Chemical' | 'Equipment'> = {
        fertilizer: 'Fertilizer',
        chemical: 'Chemical',
        equipment: 'Equipment',
      };
      return (await actionStock(categoryMap[tab], action, payload)) as IApiResponse;
    },
    onSuccess: (res, variables) => {
      const title = variables.action === 'Create' ? 'Sync ข้อมูลไม่สำเร็จ' : 'ดำเนินการไม่สำเร็จ';
      if (!notifyStockErrors(title, res, variables.payload)) {
        notification.success({ message: 'ดำเนินการสำเร็จ' });
      }
      refetch();
    },
    onError: (err: any) => notification.error({ message: err.message || 'Network Error' }),
  });

  const filteredData = useMemo(() => {
    if (!searchText) return selected;
    const lower = searchText.toLowerCase();
    return selected.filter((item: any) => Object.values(item).some((val) => String(val).toLowerCase().includes(lower)));
  }, [selected, searchText]);

  // Updated Mapping based on your requirements
  const getFieldKeys = () => {
    switch (tab) {
      case 'fertilizer':
        return {
          No: 'V_FER_LOT',
          name: 'V_FER_DESC',
          farmer: 'V_FER_FID',
          lot: 'V_FER_ID',
          qty: 'V_FER_OH',
          unit: 'V_FER_UM',
          tumbol: 'V_FER_TUMBOL',
          amphur: 'V_FER_AMPHUR',
          province: 'V_FER_PROVINCE',
        };
      case 'chemical':
        return {
          No: 'V_CHEM_LOT',
          name: 'V_CHEM_DESC2',
          farmer: 'V_CHEM_FID',
          lot: 'V_CHEM_ID',
          qty: 'V_CHEM_OH',
          unit: 'V_CHEM_UM',
          tumbol: 'V_CHEM_TUMBOL',
          amphur: 'V_CHEM_AMPHUR',
          province: 'V_CHEM_PROVINCE',
        };
      case 'equipment':
        return {
          No: 'V_EQPT_LOT',
          name: 'V_EQPT_TYPE_NAME',
          farmer: 'V_EQPT_FID',
          lot: 'V_EQPT_ID',
          qty: 'V_EQPT_OH',
          unit: 'V_EQPT_UM',
          tumbol: 'V_EQPT_TUMBOL',
          amphur: 'V_EQPT_AMPHUR',
          province: 'V_EQPT_PROVINCE',
        };
    }
  };

  const keys = getFieldKeys();

  const columns: TableColumnsType<any> = useMemo(
    () => [
      {
        title: 'ลำดับ',
        width: 60,
        align: 'center',
        render: (_, __, i) => <b>{i + 1}</b>,
      },
      {
        title: 'รหัส',
        dataIndex: keys.No,
        sorter: (a, b) => (a[keys.No] || '').localeCompare(b[keys.No] || ''),
        filters: Array.from(new Set(selected.map((i) => i[keys.No]).filter(Boolean)))
          .sort()
          .map((v) => ({ text: String(v), value: String(v) })),
        onFilter: (v, r) => r[keys.No] === v,
        filterSearch: true,
      },
      {
        title: 'ชื่อ',
        dataIndex: keys.name,
        sorter: (a, b) => (a[keys.name] || '').localeCompare(b[keys.name] || ''),
        filters: Array.from(new Set(selected.map((i) => i[keys.name]).filter(Boolean)))
          .sort()
          .map((v) => ({ text: String(v), value: String(v) })),
        onFilter: (v, r) => r[keys.name] === v,
        filterSearch: true,
      },
      {
        title: 'เกษตรกร',
        dataIndex: keys.farmer,
        width: 180,
        sorter: (a, b) => (a[keys.farmer] || '').localeCompare(b[keys.farmer] || ''),
        filters: Array.from(new Set(selected.map((i) => i[keys.farmer]).filter(Boolean)))
          .sort()
          .map((v) => ({ text: String(v), value: String(v) })),
        onFilter: (v, r) => r[keys.farmer] === v,
        filterSearch: true,
      },
      {
        title: 'รอบการเพาะปลูก',
        dataIndex: keys.lot,
        width: 150,
        sorter: (a, b) => (a[keys.lot] || '').localeCompare(b[keys.lot] || ''),
      },
      {
        title: 'จำนวน',
        dataIndex: keys.qty,
        width: 100,
        align: 'right',
        sorter: (a, b) => (Number(a[keys.qty]) || 0) - (Number(b[keys.qty]) || 0),
        render: (v) => <b>{v ?? 0}</b>,
      },
      {
        title: 'หน่วย',
        dataIndex: keys.unit,
        width: 100,
        sorter: (a, b) => (a[keys.unit] || '').localeCompare(b[keys.unit] || ''),
      },
      {
        title: 'ตำบลที่ตั้งสวน',
        dataIndex: keys.tumbol,
        sorter: (a, b) => (a[keys.tumbol] || '').localeCompare(b[keys.tumbol] || ''),
        filters: Array.from(new Set(selected.map((i) => i[keys.tumbol]).filter(Boolean)))
          .sort()
          .map((v) => ({ text: String(v), value: String(v) })),
        onFilter: (v, r) => r[keys.tumbol] === v,
        filterSearch: true,
      },
      {
        title: 'อำเภอที่ตั้งสวน',
        dataIndex: keys.amphur,
        sorter: (a, b) => (a[keys.amphur] || '').localeCompare(b[keys.amphur] || ''),
        filters: Array.from(new Set(selected.map((i) => i[keys.amphur]).filter(Boolean)))
          .sort()
          .map((v) => ({ text: String(v), value: String(v) })),
        onFilter: (v, r) => r[keys.amphur] === v,
        filterSearch: true,
      },
      {
        title: 'จังหวัดที่ตั้งสวน',
        dataIndex: keys.province,
        sorter: (a, b) => (a[keys.province] || '').localeCompare(b[keys.province] || ''),
        filters: Array.from(new Set(selected.map((i) => i[keys.province]).filter(Boolean)))
          .sort()
          .map((v) => ({ text: String(v), value: String(v) })),
        onFilter: (v, r) => r[keys.province] === v,
        filterSearch: true,
      },
    ],
    [selected, keys],
  );

  return (
    <div style={{ padding: '20px' }}>
      <Flex justify="space-between" align="center" className="mb-3">
        <Flex vertical gap={4}>
          <Radio.Group value={tab} onChange={(e) => setTab(e.target.value)} buttonStyle="solid">
            <Radio.Button value="fertilizer">Fertilizer (ปุ๋ย)</Radio.Button>
            <Radio.Button value="chemical">Chemical (สารเคมี)</Radio.Button>
            <Radio.Button value="equipment">Equipment (อุปกรณ์)</Radio.Button>
          </Radio.Group>
          <Radio.Group value={mode} onChange={(e) => setMode(e.target.value as Mode)} size="small">
            <Radio.Button value="insert">รายการรอนำเข้า (Add)</Radio.Button>
            <Radio.Button value="update">รายการแก้ไข/ลบ (Edit/Delete)</Radio.Button>
          </Radio.Group>
        </Flex>

        <Flex gap={12} align="flex-end">
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            รีเฟรช
          </Button>
          <Input.Search
            placeholder="ค้นหา..."
            allowClear
            onSearch={setSearchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 350 }}
          />
          {mode === 'update' && (
            <button
              type="button"
              className="btn btn-outline-danger"
              disabled={!selectedRowKeys.length || stockMutation.isPending}
              onClick={() => stockMutation.mutate({ action: 'Delete', payload: selectedRows })}
            >
              ลบข้อมูล
            </button>
          )}
          <button
            type="button"
            className="btn btn-success"
            style={{ minWidth: 120 }}
            disabled={!selectedRowKeys.length || stockMutation.isPending}
            onClick={() =>
              stockMutation.mutate({
                action: mode === 'insert' ? 'Create' : 'Update',
                payload: selectedRows,
              })
            }
          >
            {stockMutation.isPending
              ? 'กำลังดำเนินการ...'
              : mode === 'insert'
                ? 'ส่งข้อมูลไป OTOD >'
                : 'แก้ไขข้อมูลที่ OTOD >'}
          </button>
        </Flex>
      </Flex>

      <Table
        rowSelection={{
          selectedRowKeys,
          onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
          },
        }}
        columns={columns}
        dataSource={filteredData}
        loading={isFetching}
        size="small"
        bordered
        rowKey="_key"
        pagination={false}
        scroll={{ x: 1000 }}
        sticky={{ offsetHeader: 0 }}
        locale={{ emptyText: 'ไม่พบข้อมูลปัจจัยการผลิต' }}
      />
    </div>
  );
};

export default StockFactorPage;
